import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { Link } from "react-router-dom";
import { Timer, Brain, AlertCircle, Clock, Zap, Users, BookOpen, ArrowRight, ArrowLeft, Sparkles, TrendingUp, Battery } from "lucide-react";
import { Helmet } from "react-helmet-async";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const tocItems = [
  { id: "introduction", title: "Introduction" },
  { id: "brain-not-built-for-endless-focus", title: "Your Brain Isn't Built for Endless Focus" },
  { id: "shorter-windows-work", title: "Why Shorter Focus Windows Work" },
  { id: "pushing-through", title: "The Problem with Pushing Through" },
  { id: "lowering-resistance", title: "Lowering the Resistance to Start" },
  { id: "quality-over-length", title: "Quality Over Length" },
  { id: "scaling-sessions", title: "Scaling Sessions with Structure" },
  { id: "when-25-isnt-enough", title: "When 25 Minutes Isn't Enough" },
  { id: "study-streams", title: "Study Streams & Shared Rhythm" },
  { id: "references", title: "References" },
];

const ScienceOfFocus = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Why does the Pomodoro Technique use 25-minute intervals?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "25 minutes is long enough to make real progress but short enough to feel low-risk. It helps overcome procrastination by reducing the mental barrier to starting, and it prevents the 'vigilance decrement'—the natural fade in attention that occurs after sustained focus."
        }
      },
      {
        "@type": "Question",
        "name": "What is vigilance decrement?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Vigilance decrement is the well-documented decline in attention and performance that occurs during prolonged tasks. Research shows that attention fades gradually over time, often without us noticing. Brief breaks reset this decline and restore focus quality."
        }
      },
      {
        "@type": "Question",
        "name": "Is 25 minutes really enough to get deep work done?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "For most tasks, yes. 25 minutes of genuine, undistracted focus often accomplishes more than 2 hours of scattered attention. For complex tasks requiring deeper flow states, many students extend to 45-50 minute intervals while maintaining the focus-rest structure."
        }
      },
      {
        "@type": "Question",
        "name": "Why do breaks improve focus instead of disrupting it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Research from the University of Illinois shows that brief diversions from a task dramatically improve sustained attention. Breaks prevent mental fatigue, protect working memory, and allow the brain to consolidate information. Skipping breaks leads to diminishing returns."
        }
      },
      {
        "@type": "Question",
        "name": "Can I use longer focus intervals than 25 minutes?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. Many students use 45/15 or 50/10 intervals for deep reading, writing, or complex problem-solving. The key is maintaining the structured alternation between focus and rest—the principle matters more than the exact numbers."
        }
      },
      {
        "@type": "Question",
        "name": "How do study streams enhance the Pomodoro Technique?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Study streams externalize the focus rhythm. Instead of managing timers alone, you follow a shared schedule with others. This removes willpower requirements, adds social accountability, and makes both focus periods and breaks feel more natural and earned."
        }
      }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The Science of Focus: Why 25 Minutes is Enough (Most of the Time)",
    "description": "Discover the neuroscience behind why the Pomodoro Technique works. Learn about vigilance decrement, cognitive load, and why structured breaks improve focus better than willpower.",
    "image": "https://csw.live/csw-logo-new.png",
    "author": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/csw-logo-new.png"
      }
    },
    "datePublished": "2025-01-18",
    "dateModified": "2025-01-18",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://csw.live/pomodoro-technique/science-of-focus/"
    },
    "keywords": ["pomodoro technique", "focus science", "vigilance decrement", "cognitive load", "attention span", "study breaks", "productivity research", "neuroscience of focus", "study stream", "online workspace", "virtual study room", "focus timer", "productivity method", "deep work", "concentration science", "attention research", "brain science", "study with me", "online study platform"]
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "The Science of Focus: Why 25 Minutes is Enough | CSW",
    "description": "Discover the neuroscience behind why the Pomodoro Technique works. Learn about vigilance decrement, cognitive load, and why structured breaks improve focus.",
    "url": "https://csw.live/pomodoro-technique/science-of-focus/",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "about": {
      "@type": "Thing",
      "name": "Focus Science"
    },
    "mentions": [
      { "@type": "Thing", "name": "Pomodoro Technique" },
      { "@type": "Thing", "name": "Vigilance Decrement" },
      { "@type": "Thing", "name": "Cognitive Load Theory" },
      { "@type": "Thing", "name": "Study Stream" },
      { "@type": "Thing", "name": "Online Workspace" },
      { "@type": "Thing", "name": "Virtual Study Room" },
      { "@type": "Thing", "name": "Focus Timer" },
      { "@type": "Thing", "name": "Productivity Research" },
      { "@type": "Thing", "name": "Attention Science" },
      { "@type": "Thing", "name": "Deep Work" },
      { "@type": "Thing", "name": "University of Illinois Research" },
      { "@type": "Thing", "name": "Brain Fatigue" }
    ],
    "keywords": "pomodoro technique, focus science, vigilance decrement, cognitive load, attention span, study breaks, productivity research, neuroscience, study stream, online workspace, virtual study room, focus timer, deep work, concentration, brain science"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://csw.live/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Pomodoro Technique",
        "item": "https://csw.live/pomodoro-technique/"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Science of Focus",
        "item": "https://csw.live/pomodoro-technique/science-of-focus/"
      }
    ]
  };

  const relatedArticles = [
    {
      title: "Pomodoro 101: What It Really Is",
      path: "/pomodoro-technique/pomodoro-101/",
      description: "Clear up common misconceptions about the technique"
    },
    {
      title: "Pomodoro vs. Procrastination",
      path: "/pomodoro-technique/pomodoro-vs-procrastination/",
      description: "How short sessions make starting easier"
    },
    {
      title: "Building Better Study Habits",
      path: "/pomodoro-technique/study-habits/",
      description: "How Pomodoro creates consistency that lasts"
    },
    {
      title: "Why Breaks Matter",
      path: "/pomodoro-technique/why-breaks-matter/",
      description: "The science behind rest and recovery"
    },
    {
      title: "Active Learning with Pomodoro",
      path: "/pomodoro-technique/active-learning/",
      description: "Transform passive reading into real understanding"
    },
    {
      title: "Pomodoro for ADHD",
      path: "/pomodoro-technique/adhd/",
      description: "How timed focus helps ADHD brains"
    },
    {
      title: "Avoid Burnout with Pomodoro",
      path: "/pomodoro-technique/avoid-burnout/",
      description: "Sustainable studying that protects your energy"
    },
    {
      title: "Pomodoro + Study Streams",
      path: "/pomodoro-technique/study-streams/",
      description: "Why timed focus works better with others"
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>
      <SEO 
        title="The Science of Focus: Why 25 Minutes is Enough | CSW"
        description="Discover the neuroscience behind why the Pomodoro Technique works. Learn about vigilance decrement, cognitive load, and why structured breaks improve focus."
        canonicalUrl="/pomodoro-technique/science-of-focus/"
      />
      <Header />
      <BlogReadingProgress />
      
      {/* Hero Section */}
      <main className="min-h-screen bg-background">
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
          
          <div className="container relative z-10 px-4 md:px-6">
            <Breadcrumb className="mb-8">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/pomodoro-technique/" className="text-muted-foreground hover:text-primary transition-colors">Pomodoro Technique</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-foreground font-medium">Science of Focus</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
                <Brain className="w-4 h-4" />
                <span>Research</span>
              </div>
              
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                The Science of Focus: Why 25 Minutes is{" "}
                <span className="text-primary">Enough</span>
              </h1>
              
              <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                The Pomodoro Technique isn't just a productivity trick—it's grounded in how your brain 
                actually handles attention, fatigue, and recovery. Here's the science behind why it works.
              </p>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="pb-24 relative">
          <div className="container mx-auto px-4">
            <div className="flex gap-12 max-w-6xl mx-auto">
              {/* Sidebar TOC - Desktop */}
              <aside className="hidden lg:block w-64 flex-shrink-0">
                <div className="sticky top-24">
                  <BlogTableOfContents items={tocItems} />
                </div>
              </aside>

              {/* Main Content */}
              <div className="flex-1 max-w-3xl">
                {/* Table of Contents - Mobile */}
                <div className="lg:hidden mb-12">
                  <BlogTableOfContents items={tocItems} />
                </div>

                {/* Introduction */}
                <section id="introduction" className="mb-16 scroll-mt-28">
                  <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
                    If you've tried the Pomodoro Technique and wondered, <em>"Is 25 minutes really optimal?"</em>—you're asking the right question.
                  </p>

                  <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                    The truth is, 25 minutes isn't magic. It's not based on a single study or a universal truth about human cognition. But it works—and not by accident.
                  </p>

                  <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                    Let's look at what the research actually says about attention, breaks, and why shorter focus windows often beat longer ones.
                  </p>
                </section>

                {/* Brain Not Built for Endless Focus */}
                <div id="brain-not-built-for-endless-focus" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Brain className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Your Brain Isn't Built for Endless Focus
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  One of the most consistent findings in attention research is something called <strong>vigilance decrement</strong>—the gradual decline in performance during prolonged, continuous tasks.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Studies by <a href="https://pubmed.ncbi.nlm.nih.gov/18348531/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Helton & Warm (2008)</a> and others show that <strong>attention fades over time</strong>—often without us noticing. This is why you can "study" for two hours and barely remember any of it.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-8">
                  <p className="text-lg font-body text-foreground/90 italic text-center">
                    Sustained attention tasks show measurable performance declines after just 10–15 minutes of uninterrupted focus.
                  </p>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  Pomodoro counteracts this by <strong>resetting your attention</strong> before the decline kicks in. It's not about working harder—it's about working in rhythm with your brain's natural limitations.
                </p>

                {/* Shorter Windows Work */}
                <div id="shorter-windows-work" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Timer className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Why Shorter Focus Windows Work Better
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  A landmark study from the <a href="https://news.illinois.edu/view/6367/205427" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">University of Illinois (Ariga & Lleras, 2011)</a> found that <strong>brief diversions from a task dramatically improve focus</strong> during prolonged work.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Participants who took short breaks performed significantly better on attention-demanding tasks than those who worked straight through. The conclusion:
                </p>

                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-6 mb-8">
                  <div className="flex items-start gap-4">
                    <Sparkles className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-display font-semibold text-foreground mb-2">The Key Insight</h4>
                      <p className="text-muted-foreground">
                        Breaks don't interrupt focus—they <strong>protect it</strong>. Short, planned interruptions prevent the mental fatigue that leads to distraction and errors.
                      </p>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  This is exactly what the Pomodoro Technique does: it builds recovery into the structure, so you don't burn through your cognitive reserves. Learn more in our guide to <Link to="/pomodoro-technique/why-breaks-matter/" className="text-primary hover:underline">why breaks matter</Link> and how they help you <Link to="/pomodoro-technique/avoid-burnout/" className="text-primary hover:underline">avoid burnout</Link>.
                </p>

                {/* Pushing Through */}
                <div id="pushing-through" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-7 h-7 text-destructive" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        The Problem with "Pushing Through"
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Most students believe that if they just <em>try harder</em>, they can focus longer. But willpower is a limited resource.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on <strong>ego depletion</strong> (<a href="https://pubmed.ncbi.nlm.nih.gov/9599441/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Baumeister et al., 1998</a>) shows that self-control draws from a finite pool. Every time you resist a distraction or force yourself to stay on task, you spend some of that pool.
                </p>

                <div className="bg-muted/30 border border-border rounded-xl p-6 mb-8">
                  <h3 className="font-display font-bold text-lg mb-4">What happens when you "push through":</h3>
                  <ul className="space-y-3 text-foreground/90 font-body">
                    <li className="flex items-start gap-3">
                      <span className="text-destructive mt-1">•</span>
                      <span>Focus degrades quietly—you don't always notice</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-destructive mt-1">•</span>
                      <span>Working memory fills up, reducing comprehension</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-destructive mt-1">•</span>
                      <span>Errors increase while perceived effort stays the same</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-destructive mt-1">•</span>
                      <span>You end up tired <em>and</em> unproductive</span>
                    </li>
                  </ul>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  The Pomodoro Technique sidesteps this by <strong>externalizing the structure</strong>. You don't rely on willpower to decide when to stop—you follow the timer.
                </p>

                {/* Lowering Resistance */}
                <div id="lowering-resistance" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Lowering the Resistance to Start
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  One of the most powerful effects of the 25-minute interval is psychological, not neurological.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Telling yourself "I'll study for three hours" feels overwhelming. Telling yourself "I'll study for 25 minutes" feels doable. This <strong>lowers the activation threshold</strong>—the mental barrier to starting.
                </p>

                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-5">
                    <h4 className="font-display font-semibold mb-2 text-destructive">High Resistance</h4>
                    <p className="text-muted-foreground text-sm">"I need to study all afternoon"</p>
                    <p className="text-muted-foreground text-sm mt-2">→ Procrastination, avoidance</p>
                  </div>
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-5">
                    <h4 className="font-display font-semibold mb-2 text-primary">Low Resistance</h4>
                    <p className="text-muted-foreground text-sm">"I'll do one 25-minute session"</p>
                    <p className="text-muted-foreground text-sm mt-2">→ Action, momentum</p>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  Once you start, momentum builds. The first Pomodoro is often the hardest—after that, continuing feels easier than stopping.
                </p>

                {/* Quality Over Length */}
                <div id="quality-over-length" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Quality Over Length
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Here's the counterintuitive truth:
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-8">
                  <p className="text-lg font-body text-foreground/90 italic text-center">
                    <strong>25 minutes of genuine focus often accomplishes more than 2 hours of scattered attention.</strong>
                  </p>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on multitasking (<a href="https://www.pnas.org/doi/10.1073/pnas.0903620106" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Ophir et al., 2009</a>) shows that frequent task-switching degrades memory, comprehension, and accuracy. Even brief interruptions—like checking your phone—can take <strong>up to 23 minutes to fully recover from</strong>.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  The Pomodoro Technique forces single-task focus. During those 25 minutes, you're not allowed to switch. That constraint is what makes the time count.
                </p>

                {/* Scaling Sessions */}
                <div id="scaling-sessions" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Battery className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Scaling Sessions with Structure
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  A common concern: "25 minutes isn't enough to study seriously."
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  But here's how Pomodoro actually works in practice:
                </p>

                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 my-8">
                  <h3 className="font-display text-lg font-semibold text-foreground mb-4">4 Pomodoros = 100 Minutes of Deep Work</h3>
                  <ul className="space-y-3 text-foreground/90 font-body">
                    <li className="flex items-center gap-3">
                      <span className="bg-primary/10 text-primary px-2 py-1 rounded text-sm font-medium">25 min</span>
                      <span>Focus</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="bg-muted text-muted-foreground px-2 py-1 rounded text-sm font-medium">5 min</span>
                      <span>Break</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="bg-primary/10 text-primary px-2 py-1 rounded text-sm font-medium">25 min</span>
                      <span>Focus</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="bg-muted text-muted-foreground px-2 py-1 rounded text-sm font-medium">5 min</span>
                      <span>Break</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="bg-primary/10 text-primary px-2 py-1 rounded text-sm font-medium">25 min</span>
                      <span>Focus</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="bg-muted text-muted-foreground px-2 py-1 rounded text-sm font-medium">5 min</span>
                      <span>Break</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="bg-primary/10 text-primary px-2 py-1 rounded text-sm font-medium">25 min</span>
                      <span>Focus</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="bg-secondary/20 text-secondary-foreground px-2 py-1 rounded text-sm font-medium">15-30 min</span>
                      <span>Long break</span>
                    </li>
                  </ul>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  That's nearly two hours of <strong>high-quality</strong> study time—without the fatigue that comes from grinding through uninterrupted.
                </p>

                {/* When 25 Isn't Enough */}
                <div id="when-25-isnt-enough" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-7 h-7 text-secondary-foreground" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        When 25 Minutes Isn't Enough
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  For some tasks—deep reading, complex writing, hard problem-solving—25 minutes can feel too short. You're just getting into flow when the timer goes off.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  That's fine. <strong>The principle matters more than the number.</strong>
                </p>

                <div className="bg-muted/30 border border-border rounded-xl p-6 mb-8">
                  <h3 className="font-display text-lg font-semibold text-foreground mb-4">Common Variations</h3>
                  <ul className="space-y-3 text-foreground/90 font-body">
                    <li className="flex items-center gap-3">
                      <span className="bg-primary/10 text-primary px-2 py-1 rounded text-sm font-medium">45/15</span>
                      <span>Good for writing and deep reading</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="bg-primary/10 text-primary px-2 py-1 rounded text-sm font-medium">50/10</span>
                      <span>Popular in study streams and academic work</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="bg-primary/10 text-primary px-2 py-1 rounded text-sm font-medium">90/20</span>
                      <span>Aligned with ultradian rhythms (advanced users)</span>
                    </li>
                  </ul>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  What matters is the <strong>structured alternation</strong> between focus and rest. Experiment to find your optimal interval—but don't abandon the rhythm.
                </p>

                {/* Study Streams */}
                <div id="study-streams" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Users className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Study Streams & Shared Rhythm
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  The science behind Pomodoro explains <em>why</em> it works. But the hardest part is still <strong>doing it consistently</strong>.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  That's where study streams come in.
                </p>

                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  <div className="bg-background border border-border rounded-xl p-5">
                    <h4 className="font-display font-semibold mb-2">External timer</h4>
                    <p className="text-muted-foreground text-sm">The room manages the rhythm—you just follow along.</p>
                  </div>
                  <div className="bg-background border border-border rounded-xl p-5">
                    <h4 className="font-display font-semibold mb-2">Social presence</h4>
                    <p className="text-muted-foreground text-sm">Others can see you, which naturally reduces distraction.</p>
                  </div>
                  <div className="bg-background border border-border rounded-xl p-5">
                    <h4 className="font-display font-semibold mb-2">Synchronized breaks</h4>
                    <p className="text-muted-foreground text-sm">Resting feels earned when everyone pauses together.</p>
                  </div>
                  <div className="bg-background border border-border rounded-xl p-5">
                    <h4 className="font-display font-semibold mb-2">Lower friction</h4>
                    <p className="text-muted-foreground text-sm">Starting is easier when it's a shared ritual, not a solo decision.</p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-xl p-6 my-8">
                  <div className="flex items-start gap-4">
                    <Sparkles className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-display font-semibold text-foreground mb-2">CSW.live: Built for Focus</h4>
                      <p className="text-muted-foreground text-sm mb-4">
                        CSW.live provides silent, camera-on study rooms with synchronized Pomodoro timers. 
                        Join a room, follow the rhythm, and let the structure do the work.
                      </p>
                      <a 
                        href="https://csw.live" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-primary text-sm font-medium hover:underline"
                      >
                        Try a Study Session <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* References */}
                <div id="references" className="bg-card border border-border rounded-2xl p-8 mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-7 h-7 text-muted-foreground" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight">
                        References
                      </h2>
                    </div>
                  </div>
                  
                  <ul className="space-y-3 text-muted-foreground font-body text-sm">
                    <li>
                      <a href="https://pubmed.ncbi.nlm.nih.gov/21211793/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Ariga & Lleras (2011) — Brief and rare mental breaks keep you focused (Cognition)
                      </a>
                    </li>
                    <li>
                      <a href="https://pubmed.ncbi.nlm.nih.gov/18348531/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Helton & Warm (2008) — Signal salience and the mindlessness theory of vigilance
                      </a>
                    </li>
                    <li>
                      <a href="https://news.illinois.edu/view/6367/205427" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        University of Illinois (2011) — Brief diversions improve focus
                      </a>
                    </li>
                    <li>
                      <a href="https://www.pnas.org/doi/10.1073/pnas.0903620106" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Ophir, Nass, & Wagner (2009) — Cognitive control in media multitaskers (PNAS)
                      </a>
                    </li>
                    <li>
                      <a href="https://pubmed.ncbi.nlm.nih.gov/9599441/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Baumeister et al. (1998) — Ego depletion and self-control
                      </a>
                    </li>
                  </ul>
                </div>

                {/* Continue Reading Section */}
                <section className="bg-gradient-to-br from-primary/10 via-secondary/5 to-primary/10 rounded-3xl p-8 md:p-12 mt-12">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <BookOpen className="w-6 h-6 text-primary" />
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground m-0">
                      Continue Reading
                    </h2>
                  </div>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {relatedArticles.map((article, index) => (
                      <Link 
                        key={index}
                        to={article.path}
                        className="group bg-background/80 backdrop-blur-sm hover:bg-background border border-border hover:border-primary/50 rounded-2xl p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                      >
                        <h3 className="font-display font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-4">{article.description}</p>
                        <span className="inline-flex items-center gap-2 text-primary text-sm font-medium">
                          Read more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </Link>
                    ))}
                  </div>
                </section>

                {/* Back to Pomodoro Technique */}
                <div className="mt-12 pt-8 border-t border-border">
                  <Link 
                    to="/pomodoro-technique/" 
                    className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Pomodoro Technique
                  </Link>
                </div>

              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default ScienceOfFocus;
